import type { Root } from './prototypeTypes';

function feedbackView(root: Root): HTMLElement | null {
	return root.querySelector<HTMLElement>('#view-feedback');
}

function submitButton(root: Root): HTMLButtonElement | null {
	return root.querySelector<HTMLButtonElement>('#feedbackSubmitBtn');
}

function feedbackSubmitIdleLabel(button: HTMLButtonElement): string {
	return button.dataset.idleLabel ?? 'Submit Feedback';
}

function setFeedbackSubmitUploading(root: Root, isUploading: boolean): void {
	const button = submitButton(root);
	if (!button) return;
	if (isUploading) {
		button.disabled = true;
		button.setAttribute('aria-busy', 'true');
		button.textContent = 'Uploading screenshot…';
		return;
	}
	if (!button.hasAttribute('data-locked')) {
		button.disabled = false;
		button.removeAttribute('aria-busy');
		button.textContent = feedbackSubmitIdleLabel(button);
	}
}

/** Starts a generation-scoped upload so reset/new feedback cannot inherit stale attachment results. */
export function beginFeedbackAttachmentUpload(root: Root): number {
	const view = feedbackView(root);
	if (!view) return 0;
	const generation = Number.parseInt(view.dataset.feedbackUploadGeneration ?? '0', 10) + 1;
	view.dataset.feedbackUploadGeneration = String(generation);
	view.dataset.feedbackUploadPending = 'true';
	setFeedbackSubmitUploading(root, true);
	return generation;
}

export function isCurrentFeedbackAttachmentUpload(root: Root, generation: number): boolean {
	const view = feedbackView(root);
	return Boolean(view && view.dataset.feedbackUploadPending === 'true' && view.dataset.feedbackUploadGeneration === String(generation));
}

export function hasPendingFeedbackAttachmentUpload(root: Root): boolean {
	return feedbackView(root)?.dataset.feedbackUploadPending === 'true';
}

export function finishFeedbackAttachmentUpload(root: Root, generation: number): void {
	if (!isCurrentFeedbackAttachmentUpload(root, generation)) return;
	const view = feedbackView(root);
	if (view) delete view.dataset.feedbackUploadPending;
	setFeedbackSubmitUploading(root, false);
}

/** Invalidates any in-flight upload and releases the form for a new feedback draft. */
export function invalidateFeedbackAttachmentUpload(root: Root): void {
	const view = feedbackView(root);
	if (!view) return;
	const generation = Number.parseInt(view.dataset.feedbackUploadGeneration ?? '0', 10) + 1;
	view.dataset.feedbackUploadGeneration = String(generation);
	delete view.dataset.feedbackUploadPending;
	const uploadZone = root.querySelector<HTMLElement>('#feedbackUploadZone');
	if (uploadZone) uploadZone.style.pointerEvents = '';
	setFeedbackSubmitUploading(root, false);
}
